
import { defineComponent } from "vue";
import AppFooter from "@/components/common/AppFooter.vue";
import { mapGetters, mapMutations } from "vuex";
import { iBenefitSummary, iCalculateRate } from "@/types/iBenefitsData";
import common from "@/config/common";
import helper from "@/utils/helper";
import benefitMapper from "@/utils/benefitMapper";
import messages from "@/config/messages";
import { CARD_CHECKED } from "@/utils/constants";
import eventBus from "@/utils/eventBus";
import * as apiClient from "@/apiclient/";
import iCOBRABenefit from "@/types/iCOBRABenefit";

interface iDependent {
  electionDependentId?: number;
  firstName?: string;
  lastName?: string;
  relationshipName?: string;
  isSelected?: boolean;
  disabled?: boolean;
  dependentCount?: number;
}

export default defineComponent({
  components: { AppFooter },
  data() {
    return {
      accordionTitle: "Accordion Title",
      benefits: [] as iCOBRABenefit[],
      dependents: [] as iDependent[],
      paymentMethods: {} as any,
      profile: {} as apiClient.ProfileResponseDto,
      common,
      validationMsg: "",
      helper,
      action: { enroll: "enroll", waive: "waive" },
      calculateRates: [] as iCalculateRate[],
    };
  },
  async created() {
    eventBus.emit("toggle-enrollments-menu", false);
    await this.getPaymentMethods();
    await this.getAllBenefits();
    await this.getAllDependents();
    await this.getProfileInfo();
    await this.calculateBenefitRates();
    window.addEventListener("beforeunload", this.confirmBeforeUnload);
  },
  beforeUnmount() {
    // Remove the beforeunload event handler when the component is destroyed
    window.removeEventListener("beforeunload", this.confirmBeforeUnload);
  },
  beforeRouteLeave(to, from, next) {
    helper.beforeRouterLeave(next);
  },
  computed: {
    ...mapGetters(["getBenefitAmount", "getFormDirty", "getEnrollment"]),
    benefitsValidationMessage() {
      return this.getBenefitsValidationMessage();
    },
  },
  methods: {
    ...mapMutations(["setBenefitAmount", "setFormDirty", "setBenefitRates"]),
    confirmBeforeUnload(event: any) {
      helper.confirmBeforeUnload(event);
    },
    toggleAttribute(isOpen: boolean) {
      return isOpen ? "collapse" : "";
    },
    async getPaymentMethods() {
      const result = await apiClient.paymentController().getApiPaymentType();
      if (!result || result.error) {
        if (result.error) {
          this.validationMsg = result.error + messages.errorMsg;
        }
        return;
      } else if (result.data?.value) {
        this.paymentMethods = result.data.value;
      }
    },
    mapApiDependentsToDependents(
      apiDependents: apiClient.DependentResponseDto[]
    ): iDependent[] {
      return apiDependents.map((dependent) => {
        return {
          electionDependentId: dependent.electionDependentId!,
          firstName: dependent.firstName!,
          lastName: dependent.lastName!,
          relationshipName: "",
          dependentCount: 0,
          isSelected: false,
          disabled: false,
          customerParticipantId: dependent.customerParticipantId!,
        };
      });
    },
    async tierSelected(
      isSelected: boolean,
      tierPrice: number,
      benefitId: number,
      planDescription: string,
      index: number,
      optionIndex: number
    ) {
      this.benefits[index].dependentValMsg = "";
      this.setFormDirty(true);
      if (
        this.benefits[index] &&
        Array.isArray(this.benefits[index].dependents)
      ) {
        this.benefits[index].dependents.map((dependent: iDependent) => {
          dependent.isSelected = false;
          return dependent;
        });
      }
      if (isSelected) {
        this.benefits[index].isWaived = false;

        const existingBenefit = this.calculateRates.find(
          (item) => item.benefitId === benefitId
        );
        if (existingBenefit) {
          existingBenefit.tierLevelName =
            this.benefits[index].coverageOptions[optionIndex].tierLevelName!;
        } else {
          this.calculateRates.push({
            benefitId,
            tierLevelName:
              this.benefits[index].coverageOptions[optionIndex].tierLevelName!,
          });
        }
        this.calculateBenefitRates();
        this.benefits[index].coverageOptions.map((option, index) => {
          if (index !== optionIndex) {
            option.isSelected = false;
          }
          return option;
        });

        this.disableDependents(index, optionIndex);
        var allDisabled = false;
        if (
          this.benefits[index] &&
          Array.isArray(this.benefits[index].dependents)
        ) {
          allDisabled = this.benefits[index].dependents.every(
            (dependent: iDependent) => {
              dependent.disabled;
            }
          );
        }
        if (
          this.benefits[index].coverageOptions[optionIndex].dependentCount! >
            0 &&
          allDisabled
          // ToDO
          // ||
          // (this.benefits[index].coverageOptions[optionIndex].dependentCount >
          //   1 &&
          //   this.benefits[index].coverageOptions[optionIndex].includeSpouse &&
          //   (!this.benefits[index].dependents.some(
          //     (d) => d.relationshipName === common.dependentRelations.spouse
          //   ) ||
          //     !this.benefits[index].dependents.some(
          //       (d) =>
          //         d.relationshipName === common.dependentRelations.daughter ||
          //         d.relationshipName === common.dependentRelations.son
          //     )))
        ) {
          this.benefits[index].dependentValMsg = messages.dependentValMsg;
        }
      } else {
        if (
          this.benefits[index] &&
          Array.isArray(this.benefits[index].dependents)
        ) {
          this.benefits[index].dependents.map((dependent) => {
            dependent.disabled = true;
            return dependent;
          });
        }

        const indexToRemove = this.calculateRates.findIndex(
          (item: iCalculateRate) => item.benefitId === benefitId
        );
        if (indexToRemove !== -1) {
          this.calculateRates.splice(indexToRemove, 1);
          this.calculateBenefitRates();
        }
      }
    },
    async calculateBenefitRates() {
      const params = {
        amountDueBenefits: this.calculateRates,
        customerParticipantId: this.getEnrollment.customerParticipantId,
      };
      const result = await apiClient
        .benefitsController()
        .postApiBenefitsCalculateRates(params);

      if (!result || result.error) {
        if (result.error) {
          this.validationMsg = result.error + messages.errorMsg;
        }
        return;
      } else if (result.data) {
        this.setBenefitRates(result.data);
      }
    },
    toggleAccordionOpen(index: number) {
      this.benefits[index].isOpen = !this.benefits[index].isOpen;
    },
    async toggleAccordion(index: number, action: string) {
      let isWaived: boolean | null = false;
      if (action === this.action.waive) {
        this.benefits[index].dependentValMsg = "";
        this.setFormDirty(true);
        isWaived = this.benefits[index].isWaived!;
        this.benefits[index].isOpen = false;
        this.benefits[index].isWaived = true;
        if (
          this.benefits[index] &&
          Array.isArray(this.benefits[index].dependents)
        ) {
          this.benefits[index].dependents.map((dependent) => {
            dependent.disabled = true;
            return dependent;
          });
        }
        this.benefits[index].coverageOptions.map((option) => {
          option.isSelected = false;
          return option;
        });
        const indexToRemove = this.calculateRates.findIndex(
          (item: iCalculateRate) =>
            item.benefitId === this.benefits[index].benefitId
        );
        if (indexToRemove !== -1) {
          this.calculateRates.splice(indexToRemove, 1);
          this.calculateBenefitRates();
        }
      }
      if (action === this.action.enroll) {
        this.setFormDirty(true);
        this.benefits[index].isOpen = true;

        if (
          this.benefits[index].isWaived &&
          (this.benefits[index].benefitRateTypeId === common.benefit.single ||
            this.benefits[index].benefitRateTypeId === common.benefit.unique)
        ) {
          if (
            this.benefits[index] &&
            Array.isArray(this.benefits[index].dependents)
          ) {
            this.benefits[index].dependents.map((dependent) => {
              dependent.disabled = false;
              return dependent;
            });
          }
        }
        this.benefits[index].isWaived = false;
        if (
          this.benefits[index].benefitRateTypeId === common.benefit.single ||
          this.benefits[index].benefitRateTypeId === common.benefit.unique
        ) {
          // Only add if not already present
          if (
            !this.calculateRates.some(
              (item) => item.benefitId === this.benefits[index].benefitId
            )
          ) {
            this.calculateRates.push({
              benefitId: this.benefits[index].benefitId,
            });
          }
          this.calculateBenefitRates();
        }
      }
      let benefitTotal = this.getBenefitAmount;
      let indexToUpdate = benefitTotal.findIndex(
        (benefit: { benefitId: number }) =>
          benefit.benefitId === this.benefits[index].benefitId
      );
      if (action === this.action.waive && isWaived === false) {
        if (
          this.benefits[index] &&
          Array.isArray(this.benefits[index].dependents)
        ) {
          this.benefits[index].dependents.map((dependent) => {
            dependent.isSelected = false;
            return dependent;
          });
        }
      }
    },
    async getBenefitSummary(): Promise<void> {
      const params = {
        customerParticipantId: this.getEnrollment.customerParticipantId,
      };
      const result = await apiClient.paymentController().getApiPayment(params);
      if (!result || result.error) {
        if (result.error) {
          this.validationMsg = result.error + messages.errorMsg;
        }
        return;
      } else if (result.data) {
        helper.setBenefitSummary(result.data.benefitSummary!);
      }
    },
    async getAllBenefits() {
      const params = {
        customerParticipantId: this.getEnrollment.customerParticipantId,
      };
      const result = await apiClient
        .benefitsController()
        .getApiBenefits(params);
      if (result.data) {
        this.benefits =
          benefitMapper.mapCOBRAApiBenefitsToBenefits(result.data) ?? [];
        this.benefits.map((benefit) => {
          benefit.isOpen = false;
          if (benefit.isWaived === false) {
            const coverageOption = benefit.coverageOptions.find(
              (option) => option.isSelected === true
            );
            const benefitRate = {
              benefitId: benefit.benefitId,
            };
            if (
              coverageOption &&
              benefit.benefitRateTypeId === common.benefit.tier
            ) {
              Object.assign(benefitRate, {
                tierLevelName: coverageOption?.tierLevelName,
              });
            }
            this.calculateRates.push(benefitRate);
          }
        });
        const params = {
          customerParticipantId: this.getEnrollment.customerParticipantId,
        };
        const summary = await apiClient
          .paymentController()
          .getApiPayment(params);
        if (
          summary.data?.getAmountDueToday?.payMethod ===
          this.paymentMethods.Card
        ) {
          eventBus.emit(CARD_CHECKED, false);
        }
        if (summary.data && summary.data?.benefitSummary) {
          helper.setBenefitSummary(summary.data?.benefitSummary);
        }
      } else {
        const benefitSummary: iBenefitSummary = {
          amountDueToday: 0,
          monthlyPremium: 0,
          amountDuePlanRates: [],
          monthlyPremiumPlanRates: [],
        };
        this.setBenefitRates(benefitSummary);
      }
    },
    async getAllDependents() {
      const params = {
        customerParticipantId: this.getEnrollment.customerParticipantId,
      };
      const result = await apiClient
        .dependentController()
        .getApiDependentGetAll(params);
      if (result.data) {
        this.dependents = this.mapApiDependentsToDependents(result.data) ?? [];
        this.benefits = this.benefits.map((benefit) => {
          if (!Array.isArray(benefit.electionDependentBenefits)) {
            benefit.electionDependentBenefits = [];
          }
          benefit.dependents = this.dependents.map((dep) => ({ ...dep }));
          benefit.dependents = benefit.dependents.map(
            (dependent: iDependent) => {
              if (benefit.benefitRateTypeId === common.benefit.tier) {
                const coverageOption = benefit.coverageOptions.find(
                  (option) => option.isSelected === true
                );
                if (
                  !coverageOption ||
                  // (!coverageOption.includeSpouse &&
                  //   dependent.relationshipName ===
                  //     common.dependentRelations.spouse)
                  //  ||
                  coverageOption.dependentCount! < 1 ||
                  // (coverageOption.includeSpouse &&
                  //   coverageOption.dependentCount === 1 &&
                  //   dependent.relationshipName !==
                  //     common.dependentRelations.spouse) ||
                  (benefit.electionDependentBenefits!.indexOf(
                    dependent.electionDependentId!
                  ) === -1 &&
                    coverageOption.dependentCount ===
                      benefit.electionDependentBenefits!.length)
                  //  ||
                  // (coverageOption.includeSpouse &&
                  //   coverageOption.dependentCount > 1 &&
                  //   coverageOption.dependentCount -
                  //     benefit.electionDependentBenefits.length ===
                  //     1 &&
                  //   dependent.relationshipName !==
                  //     common.dependentRelations.spouse &&
                  //   benefit.electionDependentBenefits.indexOf(
                  //     dependent.electionDependentId
                  //   ) === -1 &&
                  //   benefit.electionDependentBenefits.every((depId) =>
                  //     benefit.dependents.some(
                  //       (d) =>
                  //         depId === d.electionDependentId &&
                  //         d.relationshipName !==
                  //           common.dependentRelations.spouse
                  //     )
                  //   ))
                ) {
                  dependent.disabled = true;
                } else {
                  dependent.disabled = false;
                }
              } else {
                dependent.disabled = false;
              }
              if (
                benefit.electionDependentBenefits!.indexOf(
                  dependent.electionDependentId!
                ) > -1
              ) {
                dependent.isSelected = true;
              } else {
                dependent.isSelected = false;
              }
              return { ...dependent };
            }
          );

          return { ...benefit };
        });
      }
    },
    async getProfileInfo() {
      const params = {
        customerParticipantId: this.getEnrollment.customerParticipantId,
      };
      const result = await apiClient.profileController().getApiProfile(params);
      if (result.data) {
        this.profile = result.data;
      }
    },
    disableDependents(bIndex: number, oIndex: number) {
      let disabled = false;
      if (this.benefits[bIndex].benefitRateTypeId === common.benefit.tier) {
        const coverageOption = this.benefits[bIndex].coverageOptions[oIndex];
        if (
          this.benefits[bIndex] &&
          Array.isArray(this.benefits[bIndex].dependents)
        ) {
          this.benefits[bIndex].dependents.map((dependent: iDependent) => {
            // if (
            // !coverageOption.includeSpouse &&
            // dependent.relationshipName === common.dependentRelations.spouse
            // ) {
            //   dependent.disabled = true;
            //   dependent.isSelected = false;
            // } else
            if (
              coverageOption.dependentCount! < 1
              // ||
              // (coverageOption.includeSpouse &&
              //   coverageOption.dependentCount === 1 &&
              //   dependent.relationshipName !== common.dependentRelations.spouse)
            ) {
              dependent.disabled = true;
            } else {
              dependent.disabled = false;
            }
            return dependent;
          });
        }
      }
      return disabled;
    },
    changeDependent(
      isSelected: boolean,
      bIndex: number,
      relationshipName: string
    ) {
      this.setFormDirty(true);
      const coverageOption = this.benefits[bIndex].coverageOptions.find(
        (option) => option.isSelected
      );
      const selectedDependents = this.benefits[bIndex].dependents.filter(
        (dependent) => dependent.isSelected
      );
      if (
        coverageOption !== undefined &&
        coverageOption.dependentCount === selectedDependents.length
      ) {
        if (
          this.benefits[bIndex] &&
          Array.isArray(this.benefits[bIndex].dependents)
        ) {
          this.benefits[bIndex].dependents.map((dependent) => {
            if (!dependent.isSelected) {
              dependent.disabled = true;
            }

            return { ...dependent };
          });
        }
      }
      if (!isSelected) {
        if (
          this.benefits[bIndex] &&
          Array.isArray(this.benefits[bIndex].dependents)
        ) {
          this.benefits[bIndex].dependents.map((dependent) => {
            dependent.disabled = false;
            // if (
            //   (coverageOption &&
            //     !coverageOption.includeSpouse &&
            //     dependent.relationshipName ===
            //       common.dependentRelations.spouse) ||
            //   (coverageOption &&
            //     coverageOption.includeSpouse &&
            //     coverageOption.dependentCount === 1 &&
            //     dependent.relationshipName !==
            //       common.dependentRelations.spouse) ||
            //   (coverageOption &&
            //     coverageOption.includeSpouse &&
            //     coverageOption.dependentCount > 1 &&
            //     coverageOption.dependentCount - selectedDependents.length === 1 &&
            //     dependent.relationshipName !== common.dependentRelations.spouse &&
            //     !selectedDependents.find(
            //       (dep) =>
            //         dep.electionDependentId === dependent.electionDependentId
            //     ) &&
            //     selectedDependents.every(
            //       (d) => d.relationshipName !== common.dependentRelations.spouse
            //     ))
            // ) {
            //   dependent.disabled = true;
            // } else
            // if (
            //   !(
            //     relationshipName !== common.dependentRelations.spouse &&
            //     dependent.relationshipName === common.dependentRelations.spouse
            //   )
            // ) {
            // dependent.disabled = false;
            // }
          });
        }
      } else {
        const selectedSpouse = this.benefits[bIndex].dependents.find(
          (d) =>
            d.relationshipName === common.dependentRelations.spouse &&
            d.isSelected
        );
        if (
          this.benefits[bIndex] &&
          Array.isArray(this.benefits[bIndex].dependents)
        ) {
          this.benefits[bIndex].dependents.map((dependent) => {
            if (
              // (coverageOption &&
              //   coverageOption.includeSpouse &&
              //   coverageOption.dependentCount > 1 &&
              //   coverageOption.dependentCount - selectedDependents.length === 1 &&
              //   dependent.relationshipName !== common.dependentRelations.spouse &&
              //   !selectedDependents.find(
              //     (dep) =>
              //       dep.electionDependentId === dependent.electionDependentId
              //   ) &&
              //   selectedDependents.every(
              //     (d) => d.relationshipName !== common.dependentRelations.spouse
              //   )) ||
              selectedSpouse &&
              selectedSpouse?.electionDependentId !==
                dependent.electionDependentId &&
              dependent.relationshipName === common.dependentRelations.spouse
            ) {
              dependent.disabled = true;
            }
          });
        }
      }
    },
    async submitHandler() {
      let allWaived = true;
      let params = this.benefits.map((benefit: iCOBRABenefit) => {
        if (benefit.isWaived) {
          return { benefitIDs: benefit.benefitIds, isWaived: true };
        } else if (benefit.isWaived === false) {
          allWaived = false;
          let tierlevelName = "";
          let tierCodeID = 0;
          let dependentIds: number[] = [];
          if (benefit.benefitRateTypeId === common.benefit.tier) {
            const selectedCoverage = benefit.coverageOptions.find(
              (option: apiClient.CoverageOptionsDto) => option.isSelected
            );
            if (selectedCoverage) {
              tierlevelName = selectedCoverage.tierLevelName!;
              tierCodeID = selectedCoverage.tierCodeId!;
            }
          }

          if (Array.isArray(benefit.dependents)) {
            benefit.dependents.forEach((dependent) => {
              if (dependent.isSelected) {
                dependentIds.push(dependent.electionDependentId!);
              }
            });
          }

          return {
            benefitIDs: benefit.benefitIds,
            tierlevelName,
            tierCodeID,
            electionDependentBenefits: dependentIds,
          };
        } else {
          return {};
        }
      });

      const addBenefitsDto = {
        addBenefitsDto: params,
        customerParticipantId: this.getEnrollment.customerParticipantId,
      };
      const result = await apiClient
        .benefitsController()
        .postApiBenefits(addBenefitsDto);
      if (!result || result.error) {
        if (result.error) {
          this.validationMsg = result.error + messages.errorMsg;
        }
        this.setFormDirty(false);
        return;
      } else if (result.data) {
        helper.setPageStep(this.common.pageStep.benefits);
        this.setFormDirty(false);
        let routeName = "documents";
        if (allWaived) {
          routeName = "confirmation";
          sessionStorage.setItem(
            common.storageVariables.pageStep,
            this.common.pageStep.confirmation.toString()
          );
          sessionStorage.setItem(
            common.storageVariables.allBenefitsWaived,
            "true"
          );
        } else {
          sessionStorage.removeItem(common.storageVariables.allBenefitsWaived);
        }
        this.$router.push({
          name: routeName,
        });
      }
    },
    effectiveDate(date: string) {
      const effectiveDate = new Date(date);
      effectiveDate.setDate(effectiveDate.getDate() + 1);
      return helper.formatDate(effectiveDate.toString());
    },
    getBenefitsValidationMessage() {
      if (!this.benefits.length) {
        return "No benefits available.";
      }

      const messages: string[] = [];

      for (const benefit of this.benefits) {
        if (benefit.isWaived === undefined || benefit.isWaived === null) {
          messages.push(
            'Please select "Enroll" or "Waive" for all of your benefits in order to proceed'
          );
          continue;
        }
        if (benefit.isWaived === false) {
          if (benefit.benefitRateTypeId === common.benefit.tier) {
            const selectedOption = benefit.coverageOptions.find(
              (option) => option.isSelected
            );
            if (!selectedOption) {
              messages.push(
                "Please select a coverage option for " +
                  (benefit.planDescription ?? benefit.planName ?? "plan") +
                  "."
              );
            } else if (
              selectedOption.dependentCount &&
              selectedOption.dependentCount > 0
            ) {
              const selectedDependents = (benefit.dependents || []).filter(
                (d) => d.isSelected
              );
              if (selectedOption.dependentCount > 10) {
                if (
                  selectedDependents.length === 0 ||
                  selectedDependents.length > selectedOption.dependentCount
                ) {
                  messages.push(
                    "Please select dependent(s) for " +
                      (benefit.planDescription ?? benefit.planName ?? "plan")
                  );
                }
              } else {
                if (
                  selectedDependents.length !== selectedOption.dependentCount
                ) {
                  messages.push(
                    `Please select dependent(s) for ${
                      benefit.planDescription ?? benefit.planName ?? "plan"
                    }.`
                  );
                }
              }
            }
          }
        }
      }

      const uniqueMessages = Array.from(new Set(messages));
      return uniqueMessages.join("<br/>\n");
    },
    isBenefitNotValid() {
      let notValid = false;
      if (!this.benefits.length) {
        notValid = true;
      }
      this.benefits.forEach((benefit) => {
        if (benefit.isWaived === null) {
          notValid = true;
        }
        if (
          benefit.isWaived === false &&
          benefit.benefitRateTypeId === common.benefit.tier
        ) {
          const isCoverageNotSelected = benefit.coverageOptions.every(
            (option) => option.isSelected === false
          );
          if (isCoverageNotSelected) {
            notValid = true;
          }
          const selectedCoverage = benefit.coverageOptions.find(
            (option) => option.isSelected
          );

          var hasDependent = false;
          if (benefit.dependents && Array.isArray(benefit.dependents)) {
            hasDependent = benefit.dependents?.some(
              (dependent) => dependent.isSelected
            );
          }

          if (
            selectedCoverage?.tierCodeId !== common.tierCodeId.memberOnly &&
            !hasDependent
            //  ||
            // (selectedCoverage?.includeSpouse &&
            //   (!benefit.dependents.some(
            //     (d) =>
            //       d.relationshipName === common.dependentRelations.spouse &&
            //       d.isSelected
            //   ) ||
            //     (selectedCoverage?.dependentCount > 1 &&
            //       benefit.dependents
            //         .filter(
            //           (dep) =>
            //             dep.relationshipName !==
            //             common.dependentRelations.spouse
            //         )
            //         .every((d) => !d.isSelected))))
          ) {
            notValid = true;
          }
        }
      });

      return notValid;
    },
  },
});
